/* /s/:handle — public seller profile page.
 *
 * Read-only, no auth required. Pulls from GET /api/public/seller/:handle.
 * Auth-aware: when a logged-in buyer / the seller themselves visits, the
 * sidebar swaps to a relationship card / owner-edit card.
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  ShieldCheck, MapPin, Calendar, CheckCircle2, MessageSquare, Package,
  Truck, RotateCcw, AlertTriangle, CreditCard, Clock, Sparkles, Building2,
  Globe, Mail, Edit3, ArrowUpRight, Lock, Loader2, Eye, Star, Instagram,
  Linkedin, Youtube, Facebook,
} from "lucide-react";

export default function SellerProfilePage() {
  const { handle } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr]   = useState(null);

  useEffect(() => {
    setData(null); setErr(null);
    api.get(`/public/seller/${handle}`)
       .then(r => setData(r.data))
       .catch(e => setErr(e.response?.data?.detail || "Profile not found"));
  }, [handle]);

  if (err) return (
    <div className="min-h-screen grid place-items-center p-8">
      <div className="max-w-md text-center">
        <h1 className="display text-3xl">Profile not available</h1>
        <p className="text-foreground/60 mt-3">{err}</p>
        <Link to="/" className="inline-block mt-6 text-foreground underline" data-testid="seller-profile-back">Back home</Link>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen grid place-items-center">
      <Loader2 className="w-6 h-6 animate-spin text-foreground/40"/>
    </div>
  );

  // Auth-aware viewer mode.
  const isSeller = user?.role === "seller" && user?.email && data.profile?.contact_email === user.email; // best-effort
  // Better signal: the seller's own tenant slug matches handle → owner. We don't have that on the user object,
  // so rely on a conservative heuristic — show owner controls when the visitor is a seller AND they recognise
  // their own brand. The owner-affordance also exists inside SellerSettings, so missing it here is harmless.
  const viewer = !user ? "anon" : (user.role === "buyer" ? "buyer" : (isSeller ? "seller" : "anon"));

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="seller-profile-page">
      <div className="max-w-[1240px] mx-auto px-6 sm:px-8 py-8">
        <Hero data={data} viewer={viewer}/>
        <div className="grid lg:grid-cols-[1fr_360px] gap-10 mt-10">
          <div className="space-y-10">
            <CatalogCTA handle={data.slug} sellerName={data.name}/>
            <About data={data}/>
            <Policies data={data}/>
            {data.profile.categories?.length > 0 && <Specialties cats={data.profile.categories}/>}
            <RatingsPlaceholder/>
          </div>
          <aside className="space-y-6">
            <TrustCard data={data}/>
            <SocialCard social={data.profile?.social}/>
            {viewer === "anon"   && <AnonCTA name={data.name}/>}
            {viewer === "buyer"  && <BuyerRelationshipCard slug={data.slug}/>}
            {viewer === "seller" && <SellerOwnerCard/>}
            <ContactCard data={data} viewer={viewer}/>
          </aside>
        </div>
      </div>
    </div>
  );
}

/* ── CATALOG CTA ──────────────────────────────────────────────────── */
/* Replaces the embedded shop grid on the profile/about page — we only
   surface a link to the full catalog (with live product count) so buyers
   browse, search, filter, and order on the dedicated catalog page. */
function CatalogCTA({ handle, sellerName }) {
  const [feed, setFeed] = useState(null);

  useEffect(() => {
    setFeed(null);
    api.get(`/public/seller/${handle}/products`)
       .then(r => setFeed(r.data))
       .catch(() => setFeed({ enabled: false, products: [] }));
  }, [handle]);

  if (!feed) {
    return (
      <section className="grid place-items-center py-10">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40" />
      </section>
    );
  }
  const count = (feed.products || []).length;
  if (!feed.enabled || count === 0) return null;

  return (
    <section data-testid="seller-profile-catalog-cta">
      <div className="rounded-2xl border border-border bg-white p-6 sm:p-8 flex items-center justify-between gap-6 flex-wrap">
        <div className="flex items-start gap-4 min-w-0">
          <div className="w-12 h-12 rounded-2xl bg-secondary grid place-items-center shrink-0">
            <Package className="w-5 h-5 text-foreground/60" />
          </div>
          <div className="min-w-0">
            <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">Catalog</div>
            <div className="display text-2xl mt-1 tracking-tight">{count} product{count !== 1 ? "s" : ""} available</div>
            <div className="text-[13px] text-foreground/60 mt-2 max-w-md leading-relaxed">
              Browse {sellerName ? `${sellerName}'s` : "the"} full range — search, filter, and place an order.
            </div>
          </div>
        </div>
        <Link
          to={`/s/${handle}/catalog`}
          data-testid="seller-profile-view-catalog"
          className="rounded-full bg-foreground text-background px-6 h-12 inline-flex items-center gap-2 text-sm font-medium hover:bg-foreground/90 shrink-0"
        >
          Browse catalog <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

/* ── HERO ─────────────────────────────────────────────────────────── */
function Hero({ data, viewer }) {
  const b = data.branding || {};
  const initial = (data.name || "?").slice(0, 1);
  // When the seller has a cover image, use it as the hero background with a
  // dark gradient overlay so the name + badges stay legible across any photo.
  const heroStyle = b.cover_image_url
    ? {
        backgroundImage: `linear-gradient(135deg, ${b.primary_color || "#0F1419"}ee 0%, ${b.primary_color || "#1d2530"}99 60%, ${b.primary_color || "#1d2530"}55 100%), url('${b.cover_image_url}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }
    : { background: b.primary_color
        ? `linear-gradient(135deg, ${b.primary_color}, ${b.primary_color}dd)`
        : "linear-gradient(135deg,#0F1419,#1d2530)" };
  return (
    <section className="relative rounded-3xl border border-border bg-white overflow-hidden">
      <div className="h-44 md:h-56" style={heroStyle} data-testid="seller-profile-cover"/>
      <div className="px-6 sm:px-8 pb-8 pt-6 flex items-end gap-6 flex-wrap relative">
        {/*
          Logo chip — when the seller has a SQUARE variant, render it
          inside the round-cornered 96×96 chip with `object-cover`
          (cropping is fine here because the asset is intentionally
          square). When only the wide PRIMARY logo exists, switch to a
          larger wide frame next to the chip slot so the wordmark
          doesn't get sliced into a vertical capsule.
        */}
        {b.logo_square_url ? (
          <div className="w-24 h-24 rounded-2xl bg-white shadow-sm border border-border grid place-items-center text-3xl display -mt-16 shrink-0 overflow-hidden">
            <img src={b.logo_square_url} alt={data.name} className="w-full h-full object-cover"/>
          </div>
        ) : b.logo_url ? (
          <div className="bg-white shadow-sm border border-border rounded-2xl px-4 py-3 -mt-12 shrink-0 inline-flex items-center">
            <img
              src={b.logo_url}
              alt={data.name}
              className="max-h-16 max-w-[18rem] object-contain object-left"
            />
          </div>
        ) : (
          <div className="w-24 h-24 rounded-2xl bg-white shadow-sm border border-border grid place-items-center text-3xl display -mt-16 shrink-0 overflow-hidden">
            {initial}
          </div>
        )}
        <div className="flex-1 min-w-0 pb-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="display text-3xl tracking-tight" data-testid="seller-profile-name">{data.name}</h1>
            {data.trust?.kyb_verified      && <Badge tone="ok"><ShieldCheck className="w-3 h-3"/> Verified business</Badge>}
            {data.trust?.insurance_on_file && <Badge tone="muted"><CheckCircle2 className="w-3 h-3"/> Insurance on file</Badge>}
          </div>
          {data.profile.tagline && (
            <div className="text-[14.5px] text-foreground/75 mt-1.5">{data.profile.tagline}</div>
          )}
          <div className="mt-2 flex items-center gap-x-5 gap-y-1 flex-wrap text-[13px] text-foreground/65">
            {data.profile.location     && <span className="inline-flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5"/>{data.profile.location}</span>}
            {data.profile.founded_year && <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5"/>Founded {data.profile.founded_year}</span>}
            {data.profile.website      && <a href={/^https?:/i.test(data.profile.website) ? data.profile.website : `https://${data.profile.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-foreground"><Globe className="w-3.5 h-3.5"/>{data.profile.website.replace(/^https?:\/\//, "")}</a>}
            {data.trust?.kyb_verified  && <span className="inline-flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5"/>EIN-verified</span>}
          </div>
        </div>
        {viewer === "anon" && (
          <Link to="/register?as=buyer" className="rounded-full bg-foreground text-background px-5 h-11 inline-flex items-center gap-2 text-sm font-medium hover:bg-foreground/90" data-testid="seller-profile-anon-cta">
            Sign up to order <ArrowUpRight className="w-4 h-4"/>
          </Link>
        )}
        {viewer === "buyer" && (
          <Link to="/buyer/messages" className="rounded-full bg-foreground text-background px-5 h-11 inline-flex items-center gap-2 text-sm font-medium hover:bg-foreground/90" data-testid="seller-profile-buyer-msg">
            <MessageSquare className="w-4 h-4"/> Send message
          </Link>
        )}
      </div>
    </section>
  );
}

/* ── ABOUT ────────────────────────────────────────────────────────── */
function About({ data }) {
  const txt = data.profile.about;
  if (!txt) return null;
  // Render simple paragraphs (split on double newlines).
  const paras = String(txt).split(/\n\s*\n/).filter(Boolean);
  return (
    <section data-testid="seller-profile-about">
      <SectionTitle eyebrow="About" title={`Who we are`}/>
      {paras.map((p, i) => (
        <p key={i} className={`text-[15px] leading-relaxed max-w-2xl ${i === 0 ? "text-foreground/85" : "text-foreground/70 mt-3"}`}>{p}</p>
      ))}
    </section>
  );
}

/* ── POLICIES ─────────────────────────────────────────────────────── */
function Policies({ data }) {
  const p = data.policies || {};
  const qi = p.qi_window_days ?? 7;
  const lead = p.default_lead_days;
  const terms = (p.default_terms || "").replace(/^NET_/, "Net-");
  // Phase IV — invoice validity window. Surface it as a first-class policy
  // so buyers know upfront how long they have to accept a quote.
  const iv = p.invoice_validity_days ?? 30;
  const items = [
    p.moq
      ? { i: Package,    l: "Minimum order",            v: p.moq,                                              h: "First-order minimum. Reorders may be lower — ask the seller." }
      : null,
    lead != null
      ? { i: Clock,     l: "Default lead time",         v: `${lead} business day${lead === 1 ? "" : "s"}`,    h: "From invoice paid (or Net-X terms accepted) to ready-to-ship. The clock starts when the buyer's payment lands or accepts credit terms — not at PO acceptance." }
      : null,
    { i: Package,       l: "Quality-inspection window", v: `${qi} day${qi === 1 ? "" : "s"} after delivery`, h: "Time to file a damage / shortage claim before claims auto-close." },
    { i: Clock,         l: "Invoice validity",          v: `${iv} day${iv === 1 ? "" : "s"}`,                  h: "How long an issued invoice stays open for you to pay or authorize. After this, the seller may re-issue at current pricing." },
    p.warranty
      ? { i: ShieldCheck,l: "Warranty",                  v: p.warranty,                                         h: "Seller's stated warranty — printed on every shipped doc." }
      : null,
    terms
      ? { i: CreditCard, l: "Payment terms",            v: terms,                                              h: "Default terms — your bilateral agreement may differ." }
      : null,
    data.profile?.ships_from
      ? { i: MapPin,    l: "Ships from",                v: data.profile.ships_from,                            h: "Origin point — used to estimate freight & customs." }
      : null,
    data.profile?.ships_to
      ? { i: Truck,     l: "Ships to",                  v: data.profile.ships_to,                              h: "Regions this seller fulfills into." }
      : { i: Truck,     l: "Shipping coverage",         v: "Continental US",                                   h: "Freight / international available on request." },
    data.profile?.dropship_eligible
      ? { i: Sparkles,  l: "Drop-ship",                 v: "Eligible",                                         h: "Seller fulfills directly to your end-customers." }
      : null,
    data.profile?.offers_prebook
      ? { i: Sparkles,  l: "Pre-book",                  v: "Eligible",                                         h: "Buyer can place orders before stock lands — locks the price & allocation." }
      : null,
    data.profile?.offer_seller_arranged_shipping
      ? { i: Truck,     l: "Shipping arranged",         v: "By seller",                                        h: "Seller books freight & passes through cost — no need to provide your own carrier." }
      : null,
    // Cancellation fee (paid but not yet shipped) — DISTINCT from return
    // policy (post-delivery). Surfaces the seller's actual restocking %.
    {
      i: RotateCcw,
      l: "Cancellation fee",
      v: p.restocking_fee_pct != null && Number(p.restocking_fee_pct) > 0
        ? `${p.restocking_fee_pct}% restocking fee`
        : "None",
      h: p.restocking_fee_pct != null && Number(p.restocking_fee_pct) > 0
        ? `If you cancel a paid order BEFORE it ships, a ${p.restocking_fee_pct}% restocking fee applies on the order total. Covers the seller's prep work (picking, packing, allocation). Doesn't apply once goods are in transit — those are returns, not cancellations.`
        : "Seller doesn't charge a restocking fee on pre-shipment cancellations.",
    },
    // Return policy (post-delivery) — distinct concept from cancellation.
    // Reads `policies.return_policy_days` from the public API:
    //   null → "Per bilateral agreement" fallback
    //   0    → "Final sale — no returns accepted"
    //   N    → "N-day return window"
    {
      i: AlertTriangle,
      l: "Return policy",
      v: p.return_policy_days == null
        ? "Per bilateral agreement"
        : Number(p.return_policy_days) === 0
          ? "Final sale — no returns"
          : `${p.return_policy_days}-day window`,
      h: p.return_policy_days == null
        ? "Returns and refunds AFTER delivery are handled per your buyer-seller MSA. Damaged shipments use the Claims flow (photos required within the QI window — see above)."
        : Number(p.return_policy_days) === 0
          ? "All sales final once goods are delivered. No returns or refunds for buyer's-remorse cancellations. Damaged shipments still use the Claims flow (photos required within the QI window)."
          : `Buyers can return goods within ${p.return_policy_days} days of delivery. Damaged shipments use the Claims flow (photos required within the QI window) — those don't count against the return window.`,
    },
    { i: AlertTriangle, l: "Damaged orders",            v: "Replacement or credit",                            h: "Photos required within QI window." },
  ].filter(Boolean);

  return (
    <section data-testid="seller-profile-policies">
      <SectionTitle eyebrow="Trade terms" title="Policies & inspection terms"
        sub="Everything you need to know before placing an order. These are the seller's defaults — your bilateral agreement (MSA) may override individual lines."/>
      <div className="grid sm:grid-cols-2 gap-3 mt-6">
        {items.map((it, i) => (
          <div key={i} className="rounded-2xl border border-border bg-white p-5 hover:border-foreground/30 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-secondary grid place-items-center">
                <it.i className="w-4 h-4 text-foreground/75"/>
              </div>
              <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55">{it.l}</div>
            </div>
            <div className="display text-lg mt-3">{it.v}</div>
            <div className="text-[12.5px] text-foreground/60 mt-1.5 leading-snug">{it.h}</div>
          </div>
        ))}
      </div>
      {p.terms_and_conditions && (
        <div className="mt-6 rounded-2xl border border-border bg-white p-5" data-testid="seller-profile-custom-terms">
          <div className="text-[10.5px] mono uppercase tracking-[0.16em] text-foreground/55 mb-2">Additional terms &amp; conditions</div>
          <pre className="whitespace-pre-wrap font-sans text-[13.5px] leading-relaxed text-foreground/85">{p.terms_and_conditions}</pre>
          <div className="text-[11.5px] text-foreground/50 mt-3">
            These terms appear on every invoice and pack list this seller issues.
          </div>
        </div>
      )}
    </section>
  );
}

/* ── SPECIALTIES ─────────────────────────────────────────────────── */
function Specialties({ cats }) {
  return (
    <section>
      <SectionTitle eyebrow="Categories" title="What we sell"/>
      <div className="flex flex-wrap gap-2 mt-5">
        {cats.map(t => (
          <span key={t} className="chip rounded-full border border-border bg-white px-3.5 h-8 inline-flex items-center text-[12.5px] text-foreground/80">{t}</span>
        ))}
      </div>
    </section>
  );
}

/* ── RATINGS — locked placeholder ─────────────────────────────────── */
function RatingsPlaceholder() {
  return (
    <section>
      <SectionTitle eyebrow="Reviews" title="Ratings & reviews"/>
      <div className="mt-5 rounded-2xl border border-dashed border-foreground/20 bg-white/40 p-8 text-center">
        <Lock className="w-5 h-5 text-foreground/40 mx-auto"/>
        <div className="display text-lg mt-3 text-foreground/80">Coming later</div>
        <div className="text-[13px] text-foreground/55 mt-1.5 max-w-sm mx-auto">
          Buyer reviews will turn on once we have a meaningful sample size.
        </div>
      </div>
    </section>
  );
}

/* ── SIDEBAR — TRUST CARD ─────────────────────────────────────────── */
function TrustCard({ data }) {
  const t = data.trust || {};
  const stats = [
    t.months_on_platform != null ? { l: "On platform",      v: `${t.months_on_platform} mo` } : null,
    t.orders_fulfilled  != null  ? { l: "Orders fulfilled", v: t.orders_fulfilled.toLocaleString() } : null,
    t.on_time_pct       != null  ? { l: "On-time fulfillment", v: `${t.on_time_pct}%` } : null,
  ].filter(Boolean);
  const checks = [
    t.kyb_verified      && "EIN verified · Middesk",
    t.bank_verified     && "Bank account verified · Plaid",
    t.insurance_on_file && "Insurance on file · Allianz Trade",
  ].filter(Boolean);

  if (stats.length === 0 && checks.length === 0) return null;
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5 text-accent"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Trust signals</div>
      </div>
      {stats.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mt-4">
          {stats.map(s => (
            <div key={s.l}>
              <div className="display text-xl">{s.v}</div>
              <div className="text-[11px] text-foreground/55 mt-0.5">{s.l}</div>
            </div>
          ))}
        </div>
      )}
      {checks.length > 0 && (
        <div className={`${stats.length > 0 ? "mt-5 pt-5 border-t border-border" : "mt-2"} space-y-2`}>
          {checks.map(c => (
            <div key={c} className="flex items-center gap-2 text-[12.5px] text-foreground/75">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0"/>{c}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── SIDEBAR — SOCIAL CARD ─────────────────────────────────────────
 * Renders only the platforms the seller actually filled in. Treats raw
 * handles, @-prefixed handles, and full URLs interchangeably so sellers
 * can paste whatever they have on file. */
function SocialCard({ social }) {
  if (!social || typeof social !== "object") return null;
  const platforms = [
    { key: "instagram", icon: Instagram,             host: "instagram.com",   label: "Instagram" },
    { key: "tiktok",    icon: null /* drawn below */, host: "tiktok.com",      label: "TikTok" },
    { key: "linkedin",  icon: Linkedin,              host: "linkedin.com/in", label: "LinkedIn" },
    { key: "x",         icon: null /* drawn below */, host: "x.com",           label: "X" },
    { key: "facebook",  icon: Facebook,              host: "facebook.com",    label: "Facebook" },
    { key: "youtube",   icon: Youtube,               host: "youtube.com",     label: "YouTube" },
  ];
  const items = platforms
    .map(p => ({ ...p, raw: (social[p.key] || "").trim() }))
    .filter(p => p.raw.length > 0);
  if (items.length === 0) return null;

  // Resolve a handle (and a clickable URL) from any of: @handle, handle, full URL.
  const toEntry = (p) => {
    const raw = p.raw;
    const hasProto = /^https?:\/\//i.test(raw);
    const url = hasProto
      ? raw
      : `https://${p.host.startsWith("linkedin.com") ? "www.linkedin.com" : "www." + p.host.split("/")[0]}/${raw.replace(/^@/, "").replace(/^\//, "")}`;
    const display = hasProto
      ? raw.replace(/^https?:\/\/(www\.)?/i, "").replace(/\/$/, "")
      : (raw.startsWith("@") ? raw : "@" + raw);
    return { url, display };
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-5" data-testid="seller-profile-social-card">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Find us</div>
      <ul className="mt-3 space-y-2">
        {items.map((p) => {
          const { url, display } = toEntry(p);
          const Icon = p.icon;
          return (
            <li key={p.key}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-[13px] text-foreground/80 hover:text-foreground transition-colors group"
                data-testid={`seller-profile-social-${p.key}`}
              >
                <span className="w-7 h-7 rounded-md bg-secondary grid place-items-center text-foreground/70 group-hover:bg-foreground group-hover:text-background transition-colors flex-shrink-0">
                  {Icon ? (
                    <Icon className="w-3.5 h-3.5"/>
                  ) : p.key === "x" ? (
                    <span className="font-bold text-[12px] leading-none">X</span>
                  ) : (
                    /* TikTok glyph — no lucide-react icon for it, draw a tight monogram */
                    <span className="font-bold text-[10.5px] leading-none">TT</span>
                  )}
                </span>
                <span className="font-medium">{p.label}</span>
                <span className="text-foreground/55 truncate">{display}</span>
                <ArrowUpRight className="w-3 h-3 ml-auto text-foreground/40 group-hover:text-foreground transition-colors flex-shrink-0"/>
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/* ── SIDEBAR variants ────────────────────────────────────────────── */
function AnonCTA({ name }) {
  return (
    <div className="rounded-2xl border border-border bg-foreground text-background p-5">
      <div className="display text-xl leading-snug">Ready to order from {name}?</div>
      <div className="text-[12.5px] text-background/65 mt-1.5">Sign up free in 60 seconds. Pre-approved Net-30 on day one if you qualify.</div>
      <Link to="/register?as=buyer" className="mt-4 inline-flex items-center justify-center w-full rounded-full bg-accent text-foreground px-4 h-10 text-sm font-medium hover:bg-accent/90">
        Sign up to order <ArrowUpRight className="w-4 h-4 ml-1"/>
      </Link>
      <Link to="/login" className="mt-2 inline-flex items-center justify-center w-full text-[12.5px] text-background/65 hover:text-background">Already a buyer? Sign in →</Link>
    </div>
  );
}

function BuyerRelationshipCard() {
  // We could fetch buyer-specific stats here. v1 — show a generic prompt.
  return (
    <div className="rounded-2xl border border-foreground/20 bg-secondary/60 p-5">
      <div className="flex items-center gap-2">
        <Star className="w-3.5 h-3.5 text-foreground/70"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Quick links</div>
      </div>
      <div className="mt-4 space-y-2">
        <Link to="/buyer/suppliers" className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/10 bg-white px-3.5 h-10 text-[13px] hover:border-foreground/30">
          See your history with this seller →
        </Link>
        <Link to="/buyer/messages" className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/10 bg-white px-3.5 h-10 text-[13px] hover:border-foreground/30">
          Open conversation →
        </Link>
      </div>
    </div>
  );
}

function SellerOwnerCard() {
  return (
    <div className="rounded-2xl border border-accent/40 bg-accent/10 p-5">
      <div className="flex items-center gap-2">
        <Edit3 className="w-3.5 h-3.5 text-foreground/70"/>
        <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">This is your profile</div>
      </div>
      <div className="text-[13px] text-foreground/80 mt-3 leading-relaxed">
        Buyers see this when you invite them. Edit content & policies in Settings.
      </div>
      <div className="mt-4 space-y-2">
        <Link to="/seller/settings#public-profile" className="w-full inline-flex items-center justify-between rounded-lg border border-foreground/15 bg-white px-3.5 h-10 text-[13px] hover:bg-foreground/5">
          Edit profile content →
        </Link>
      </div>
    </div>
  );
}

function ContactCard({ data, viewer }) {
  const email   = data.profile.contact_email;
  const website = data.profile.website;
  if (!email && !website) return null;
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <div className="text-[10.5px] mono uppercase tracking-[0.18em] text-foreground/55">Contact</div>
      <div className="mt-3 space-y-2 text-[13px]">
        {email   && <div className="flex items-center gap-2 text-foreground/80"><Mail className="w-3.5 h-3.5"/>{email}</div>}
        {website && <div className="flex items-center gap-2 text-foreground/80"><Globe className="w-3.5 h-3.5"/>{website.replace(/^https?:\/\//, "")}</div>}
      </div>
      {viewer === "anon" && (
        <div className="mt-4 text-[11.5px] text-foreground/55 leading-relaxed">
          Direct messaging unlocks once you sign up. Sellers' inboxes are buyer-only to prevent spam.
        </div>
      )}
    </div>
  );
}

/* ── atoms ───────────────────────────────────────────────────────── */
function Badge({ children, tone = "muted" }) {
  const cls = tone === "ok"
    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
    : "bg-secondary text-foreground/75 border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 h-6 text-[11px] mono uppercase tracking-[0.1em] ${cls}`}>
      {children}
    </span>
  );
}
function SectionTitle({ eyebrow, title, sub }) {
  return (
    <div>
      <div className="text-[10.5px] mono uppercase tracking-[0.22em] text-foreground/50">{eyebrow}</div>
      <div className="display text-2xl mt-1.5 tracking-tight">{title}</div>
      {sub && <div className="text-[13px] text-foreground/60 mt-2 max-w-2xl leading-relaxed">{sub}</div>}
    </div>
  );
}
